import logProPurchaseGetStartedClicked from '@alltrails/analytics/events/logProPurchaseGetStartedClicked';
import logProPurchasePageViewed from '@alltrails/analytics/events/logProPurchasePageViewed';
import NoSsr from '@material-ui/core/NoSsr';
import Typography from 'components/Typography';
import ProFeatureComparisonTable from 'components/ProFeatureComparisonTable';
import useLanguageRegionCode from 'hooks/useLanguageRegionCode';
import useUser from 'hooks/useUser';
import { lazy, Suspense, useEffect, ReactNode } from 'react';
import { defineMessage, FormattedMessage, useIntl } from '@alltrails/shared/react-intl';
import Logo from '@alltrails/shared/denali/components/Logo';
import Button from '@alltrails/shared/denali/components/Button';
import Texture from '@alltrails/shared/denali/components/Texture';
import Coupon from 'types/Coupon';
import CurrencyCode from 'types/CurrencyCode';
import Plan from 'types/Plan';
import { getPurchaseScreenTypePlan } from 'utils/amplitudeHelper';
import { wrapUrlSafe } from 'utils/language_support_util';
import { getPriceTotal, isTrial } from 'utils/PlanUtils';
import { MEDIA_QUERY_SMALL_DOWN } from 'utils/constants/breakpoints';
import chevronDownBouncing from './chevron-down-bouncing.svg';
import * as styles from './PlusLandingPage.module.scss';
import heroDesktopJpg from './images/hero-2800w.jpg';
import heroDesktopWebp from './images/hero-2800w.webp';
import heroDesktopAvif from './images/hero-2800w.avif';
import heroMobileJpg from './images/hero-768w.jpg';
import heroMobileWebp from './images/hero-768w.webp';
import heroMobileAvif from './images/hero-768w.avif';

const ChevronDownBouncing = lazy(() => import('./ChevronDownBouncing'));

function PrimaryLink({ buttonText, coupon, plan, to }: { buttonText: ReactNode; plan: Plan; coupon: Coupon; to: string }): JSX.Element {
  const languageRegionCode = useLanguageRegionCode();

  return (
    <div className={styles.buttonContainer}>
      {/*
        Two buttons are needed in order to show different button variants based on screen width.
        Setting the variant prop to the 'useIsMobile' value results in flickering on page load.
      */}
      <Button
        className={styles.desktopButton}
        size="lg"
        width="fullOnMobile"
        text={buttonText}
        linkInfo={{ href: wrapUrlSafe(to, languageRegionCode), useReactRouter: true }}
        onClick={() => {
          logProPurchaseGetStartedClicked({
            purchase_screen_type_plan: getPurchaseScreenTypePlan(plan, coupon)
          });
        }}
        testId="simple-pro-landing_page-primary"
        variant="accent"
      />
      <Button
        className={styles.mobileButton}
        size="lg"
        width="fullOnMobile"
        text={buttonText}
        linkInfo={{ href: wrapUrlSafe(to, languageRegionCode), useReactRouter: true }}
        onClick={() => {
          logProPurchaseGetStartedClicked({
            purchase_screen_type_plan: getPurchaseScreenTypePlan(plan, coupon)
          });
        }}
        testId="simple-pro-landing_page-primary"
        variant="primary"
        allowWrap
      />
    </div>
  );
}

// https://www.figma.com/file/gZaqAej8HzoFkyrjlQ4cNs/Integrating-Brand-Changes?node-id=1591%3A72835

type Props = {
  buttonText: ReactNode;
  coupon?: Coupon;
  headline: ReactNode;
  subhead: ReactNode;
  title: ReactNode;
  userId?: number;
  plan: Plan;
};

function SimpleProLandingPageBase({ buttonText, coupon, headline, subhead, title, userId, plan }: Props) {
  useEffect(() => {
    logProPurchasePageViewed({
      purchase_screen_type_plan: getPurchaseScreenTypePlan(plan, coupon)
    });
  }, [plan, coupon]);

  const intl = useIntl();
  const heroAltText = defineMessage({
    defaultMessage: 'A group of people hiking down a hill'
  });

  const user = useUser();

  const features = [
    { pro: false, text: <FormattedMessage defaultMessage="Navigate on the trail" /> },
    { pro: false, text: <FormattedMessage defaultMessage="Favorite trails and create lists" /> },
    { pro: true, text: <FormattedMessage defaultMessage="Download offline maps" /> },
    { pro: true, text: <FormattedMessage defaultMessage="Get alerts for wrong turns" /> },
    { pro: true, text: <FormattedMessage defaultMessage="Find trails by distance from you" /> },
    { pro: true, text: <FormattedMessage defaultMessage="Unlock more map details" /> },
    { pro: true, text: <FormattedMessage defaultMessage="Print maps for backup" /> },
    { pro: true, text: <FormattedMessage defaultMessage="Bring trails to life with 3D maps" /> },
    { pro: true, text: <FormattedMessage defaultMessage="Explore ad free" /> },
    { pro: true, text: <FormattedMessage defaultMessage="Give back with 1% for the Planet" /> }
  ];

  const params = new URLSearchParams(__AT_DATA__.search);

  if (coupon) {
    params.set('k', coupon.code);
  }

  if (userId) {
    params.set('uid', String(userId));
  }

  const search = params.toString();

  const to = user || userId ? `/plus/checkout${search.length > 0 ? `?${search}` : ''}` : `/signup${search.length > 0 ? `?${search}` : ''}`;

  return (
    <div className={styles.wrapper}>
      <div className={styles.hero}>
        <picture className={styles.picture}>
          <source media={MEDIA_QUERY_SMALL_DOWN} srcSet={heroMobileAvif} type="image/avif" />
          <source media={MEDIA_QUERY_SMALL_DOWN} srcSet={heroMobileWebp} type="image/webp" />
          <source media={MEDIA_QUERY_SMALL_DOWN} srcSet={heroMobileJpg} type="image/jpg" />
          <source srcSet={heroDesktopAvif} type="image/avif" />
          <source srcSet={heroDesktopWebp} type="image/webp" />
          <img alt={intl.formatMessage(heroAltText)} src={heroDesktopJpg} />
        </picture>
        <Texture variant="plus" className={styles.texture} />
      </div>
      <div className={styles.content}>
        <div className={styles.copy}>
          <div className={styles.headline}>{headline}</div>
          <div className={styles.title}>{title}</div>
          <div className={styles.subhead}>{subhead}</div>
          <div className={styles.button}>
            <PrimaryLink buttonText={buttonText} plan={plan} coupon={coupon} to={to} />
            <Typography className={styles.cancel} component="div" mb="8" variant="text100">
              <FormattedMessage defaultMessage="Cancel anytime." />
            </Typography>
          </div>
          <Typography className={styles.lottie} component="div" mb="8">
            <NoSsr>
              <Suspense fallback={<img alt="" src={chevronDownBouncing} />}>
                <ChevronDownBouncing />
              </Suspense>
            </NoSsr>
          </Typography>
        </div>
        <ProFeatureComparisonTable features={features} />
      </div>
    </div>
  );
}

type SimpleProLandingPageProps = {
  coupon?: Coupon;
  currencyCode: CurrencyCode;
  planTypes: Plan[];
  userId?: number;
};

export default function SimpleProLandingPage({ currencyCode, planTypes, coupon, userId }: SimpleProLandingPageProps) {
  const languageRegionCode = useLanguageRegionCode();

  const formatter = new Intl.NumberFormat(languageRegionCode, {
    style: 'currency',
    currency: currencyCode
  });

  const plan = planTypes[0];

  if (coupon) {
    const discountedPricePerYear = getPriceTotal(plan, coupon);

    // Desktop: https://zpl.io/ykXrOZJ
    // Mobile: https://zpl.io/9qgXqNp
    return (
      <SimpleProLandingPageBase
        buttonText={<FormattedMessage defaultMessage="Save today" />}
        coupon={coupon}
        headline={<FormattedMessage defaultMessage="Limited time only" />}
        subhead={
          discountedPricePerYear === 0 ? (
            <FormattedMessage
              defaultMessage="Just {discountedPricePerYear} for one year"
              values={{
                discountedPricePerYear: formatter.format(discountedPricePerYear)
              }}
            />
          ) : (
            <FormattedMessage
              defaultMessage="Just <s>{priceTotal}</s> {discountedPricePerYear} for one year (that’s {monthlyCost} a month)"
              values={{
                discountedPricePerYear: formatter.format(discountedPricePerYear),
                monthlyCost: formatter.format(discountedPricePerYear / 12),
                priceTotal: formatter.format(Number(plan.priceTotal)),
                s: (chunks: string) => <s className={styles.s}>{chunks}</s>
              }}
            />
          )
        }
        title={
          <FormattedMessage defaultMessage="Save {percent, number, ::percent} on AllTrails+" values={{ percent: coupon.discountPercentage / 100 }} />
        }
        userId={userId}
        plan={plan}
      />
    );
  }

  const annualCost = Number(plan.priceTotal);
  const monthlyCost = Number(plan.priceMonth);
  const headline = <Logo size="sm" color="dark" variant="plus-logomark" />;
  if (isTrial(plan)) {
    return (
      // Desktop: https://zpl.io/PqOJONM
      // Mobile: https://zpl.io/o1nvD3m
      <SimpleProLandingPageBase
        buttonText={<FormattedMessage defaultMessage="Start your free trial" />}
        headline={headline}
        subhead={
          <FormattedMessage
            defaultMessage="Then only {annualCost} a year (that’s {monthlyCost} a month)"
            values={{
              annualCost: formatter.format(annualCost),
              monthlyCost: formatter.format(monthlyCost)
            }}
          />
        }
        title={<FormattedMessage defaultMessage="Your first week’s on us" />}
        userId={userId}
        plan={plan}
      />
    );
  }

  return (
    // Desktop: https://zpl.io/wqB84gd
    // Mobile: https://zpl.io/p1mp10E
    <SimpleProLandingPageBase
      buttonText={<FormattedMessage defaultMessage="Get started" />}
      headline={headline}
      subhead={
        <FormattedMessage
          defaultMessage="Only {annualCost} a year (that’s {monthlyCost} a month)"
          values={{
            annualCost: formatter.format(annualCost),
            monthlyCost: formatter.format(monthlyCost)
          }}
        />
      }
      title={<FormattedMessage defaultMessage="Confidence that travels light" />}
      userId={userId}
      plan={plan}
    />
  );
}
